// Repository-sourced cross-link index.
//
// The only first-party relationship data that exists in the repo is declared
// on each Wisdom post (`relatedMusic`, `relatedBook`, `relatedActivation`).
// This module reads those declarations forwards AND backwards so every
// section can link to the sections that genuinely reference it. Nothing here
// invents a relationship — every edge comes from src/lib/content.ts.

import { ACTIVATIONS, WISDOM, type WisdomPost } from "@/lib/content";

export type CrossLink = { to: string; label: string; context: string };

/** Wisdom writings that explicitly reference a release slug. */
export function wisdomForRelease(slug: string): WisdomPost[] {
  return WISDOM.filter((w) => w.relatedMusic === slug);
}

/** Wisdom writings that explicitly reference a book slug. */
export function wisdomForBook(slug: string): WisdomPost[] {
  return WISDOM.filter((w) => w.relatedBook === slug);
}

/** Wisdom writings that explicitly reference an activation slug. */
export function wisdomForActivation(slug: string): WisdomPost[] {
  return WISDOM.filter((w) => w.relatedActivation === slug);
}

/** Title for an activation slug, when the practice exists in the canon. */
export function activationTitle(slug: string): string | null {
  return ACTIVATIONS.find((a) => a.slug === slug)?.title ?? null;
}

/** Other Wisdom writings in the same category (excluding the current one). */
export function wisdomInCategory(category: string, excludeSlug: string, limit = 3): WisdomPost[] {
  return WISDOM.filter((w) => w.category === category && w.slug !== excludeSlug).slice(0, limit);
}

/** Wisdom writings that share at least one companion work with this post. */
export function wisdomSharingCompanions(post: WisdomPost, limit = 3): WisdomPost[] {
  return WISDOM.filter(
    (w) =>
      w.slug !== post.slug &&
      ((post.relatedMusic && w.relatedMusic === post.relatedMusic) ||
        (post.relatedBook && w.relatedBook === post.relatedBook) ||
        (post.relatedActivation && w.relatedActivation === post.relatedActivation)),
  ).slice(0, limit);
}
